import React, { Suspense, useState } from 'react';
import SpinnerTw from '~/components/SpinnerTw';
import useSubscribeToEmitterEvent from '~/utils/events/use-subscribe-to-event';
import { getLcn } from '~/utils/labeled-classnames';
import { randomLogoUrl } from '~/utils/paths';
import { useCommonAppContext } from '../contexts/CommonAppContext';

const ResendConfirmationEmail = React.lazy(() => import('./ResendConfirmationEmail'))
const ConfirmEmail = React.lazy(() => import('./ConfirmEmail'))
const ResetPassword = React.lazy(() => import('./ResetPassword'));
const ResetPasswordRequest = React.lazy(() => import('./ResetPasswordRequest'));
const SignInFront = React.lazy(() => import('./SignInFront'));
const SignInWithPassword = React.lazy(() => import('./SignInWithPassword'));
const SignUpWithPassword = React.lazy(() => import('./SignUpWithPassword'));

export const signInModeChangedEvent = 'sign-in-mode-changed'

const signInModes = [
  'signInFront',
  'signUpPassword',
  'signInPassword',
  'confirmEmail',
  'resendConfirmationEmail',
  'resetPasswordRequest',
  'resetPassword',
] as const
export type SignInMode = typeof signInModes[number]

export default function SignInUpFlow({
  initialSignInMode = 'signInFront',
}: {
  initialSignInMode?: SignInMode
}) {
  const { currentSite } = useCommonAppContext();
  const [signInMode, setSignInMode] = useState<SignInMode>(initialSignInMode)

  const handleSignInModeChange = React.useCallback((signInMode: SignInMode) => {
    setSignInMode(signInMode)
  }, [])

  const lcn = getLcn({
    signInUpFlow: 'flex flex-col gap-9 text-center w-full sm:w-[300px] ',
    flowImage: ''
  });

  useSubscribeToEmitterEvent(signInModeChangedEvent, handleSignInModeChange)
  if (!currentSite) return null;

  // To enable email+password sign in for a site, add to emailPwEnabledSites
  return (
    <div className={lcn.signInUpFlow}>
      <img src={randomLogoUrl(currentSite.name)} className='mx-auto rounded-lg ' alt={`Logo for ${currentSite.name}`} />

      <Suspense fallback={<SpinnerTw />}>
        {signInMode === 'signInFront' &&
          <SignInFront />
        }
        {signInMode === 'signUpPassword' &&
          <SignUpWithPassword />
        }
        {signInMode === 'signInPassword' &&
          <SignInWithPassword />
        }
        {signInMode === 'confirmEmail' &&
          <ConfirmEmail />
        }
        {signInMode === 'resendConfirmationEmail' &&
          <ResendConfirmationEmail />
        }
        {signInMode === 'resetPasswordRequest' &&
          <ResetPasswordRequest />
        }
        {signInMode === 'resetPassword' &&
          <ResetPassword />
        }
      </Suspense>
    </div>
  )

}